"use client";

import { FiCalendar, FiClock, FiMonitor } from "react-icons/fi";
import Image from "next/image";
import { useEffect, useState } from "react";
import { CALLRAIL_HREF, CALLRAIL_PRETTY } from "@/lib/utils";
import { useRouter, useSearchParams } from "next/navigation";
import CalendlyConfirmationThankYou from "@/assets/images/assessment/calendly-confirmation-thank-you.svg";

export interface CalendlyThankYouGenericProps {
  date?: string;
  time?: string;
  appointmentType?: string;
  heading?: string;
  showImage?: boolean;
  redirectUrl?: string;
  redirectDelay?: number;
  customMessage?: string;
  useUrlParams?: boolean;
}

const CalendlyThankYouGeneric: React.FC<CalendlyThankYouGenericProps> = ({
  date: propDate,
  time: propTime,
  appointmentType: propAppointmentType = "Consultation",
  heading = "Thank You!",
  showImage = true,
  redirectUrl,
  redirectDelay = 0,
  customMessage,
  useUrlParams = true,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [eventData, setEventData] = useState({
    date: propDate,
    time: propTime,
    appointmentType: propAppointmentType,
  });

  useEffect(() => {
    // Read schedule data from URL params if enabled
    if (useUrlParams) {
      const startTime = searchParams.get("start_time");

      let parsedDate = propDate;
      let parsedTime = propTime;

      // Parse start_time parameter if provided and convert to LA timezone
      if (startTime) {
        try {
          const scheduledDate = new Date(startTime);

          // Convert to LA timezone (PST/PDT)
          const laTimeZone = "America/Los_Angeles";

          // Format date (e.g., "May 27, 2025")
          parsedDate = scheduledDate.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            timeZone: laTimeZone,
          });

          // Format time (e.g., "2:18PM PST")
          parsedTime = scheduledDate
            .toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              timeZoneName: "short",
              timeZone: laTimeZone,
            })
            .replace(/(\d+:\d+)\s+(AM|PM)/, "$1$2");
        } catch (error) {
          console.error("Error parsing start_time parameter:", error);
          // Fall back to prop values if parsing fails
          parsedDate = propDate;
          parsedTime = propTime;
        }
      }

      setEventData({
        date: parsedDate,
        time: parsedTime,
        appointmentType: propAppointmentType, // Always use prop, not URL param
      });
    } else {
      setEventData({
        date: propDate,
        time: propTime,
        appointmentType: propAppointmentType,
      });
    }
  }, [searchParams, propDate, propTime, propAppointmentType, useUrlParams]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });

    // Redirect after delay if specified
    if (redirectUrl && redirectDelay > 0) {
      const timer = setTimeout(() => {
        router.push(redirectUrl);
      }, redirectDelay);

      return () => clearTimeout(timer);
    }
  }, [redirectUrl, redirectDelay, router]);

  return (
    <div className="bg-primary-100">
      <div className="mx-auto flex max-w-lg flex-col gap-2 p-4 md:gap-0 md:px-12 md:pb-4 md:pt-10 lg:flex-row lg:justify-between lg:gap-2 lg:px-3 lg:pb-16 lg:pt-16">
        <div className="flex flex-col gap-4 md:max-w-[514px] md:gap-6 lg:max-w-[590px]">
          <div className="flex w-full flex-col gap-2 lg:w-[590px]">
            <h1 className="font-montserrat text-heading-2 uppercase text-secondary-500 md:text-heading-1">
              {heading}
            </h1>
            <p className="gap-2 font-montserrat text-subheading-3-400 text-neutral-500 md:text-subheading-2">
              You have scheduled your&nbsp;
              {eventData.appointmentType}
            </p>
          </div>

          {(eventData.time || eventData.date) && (
            <div className="col-span-5 flex flex-col gap-2">
              <p className="font-montserrat text-subheading-3-500 text-neutral-600 md:text-heading-4">
                {eventData.appointmentType} Details:
              </p>

              {eventData.time && (
                <div className="flex flex-row items-center gap-2">
                  <FiClock
                    className="size-4 text-neutral-400 md:size-6"
                    aria-label="Time"
                  />
                  <p className="font-montserrat text-subheading-3-400 text-secondary-500 md:text-subheading-2">
                    {eventData.time}
                  </p>
                </div>
              )}
              {eventData.date && (
                <div className="flex flex-row items-center gap-2">
                  <FiCalendar
                    className="size-4 text-neutral-400 md:size-6"
                    aria-label="Date"
                  />
                  <p className="font-montserrat text-subheading-3-400 text-secondary-500 md:text-subheading-2">
                    {eventData.date}
                  </p>
                </div>
              )}
              <div className="flex flex-row items-center gap-2">
                <FiMonitor
                  className="size-4 text-neutral-400 md:size-6"
                  aria-label="Zoom"
                />
                <p className="font-montserrat text-subheading-3-400 text-secondary-500 md:text-subheading-2">
                  Zoom
                </p>
              </div>
            </div>
          )}

          <p className="font-roboto text-body-1-300 text-neutral-500">
            {customMessage ||
              "Please check your email for the Zoom link and more information."}
          </p>

          <p className="mt-2 font-roboto text-body-1-300 text-neutral-500 md:mt-4 lg:mt-0">
            If you have any questions or need to reschedule,
            <br />
            please contact us at{" "}
            <span>
              <a
                className="underline"
                href={CALLRAIL_HREF}
                id="hotline-assessment-thank-you"
              >
                {CALLRAIL_PRETTY}
              </a>
            </span>
            .
          </p>
        </div>

        {showImage && (
          <Image
            className="h-[185px] w-[358px] object-contain max-lg:mx-auto md:h-[335px] md:w-[648px] lg:-ml-32 lg:h-[408px] lg:w-[798px]"
            src={CalendlyConfirmationThankYou}
            quality={100}
            priority
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            alt="Calendly Thank You Image"
          />
        )}
      </div>
    </div>
  );
};

export default CalendlyThankYouGeneric;
